import { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { usePrefs } from "@/contexts/PrefsContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

/**
 * SecurePrivacyContext — the authenticated app's Screen Privacy layer.
 *
 * Adds:
 *  - `standalone`  — true when running as an installed PWA
 *  - `require_reauth` — when on + standalone, unlock prompts for password
 *  - `print_allowed_paths` — routes the admin has opted into print export
 *  - `verifyPassword(pw)` — hits /api/auth/verify-password
 */

const DEFAULTS = {
    shield_enabled: false,
    hide_values_default: false,
    watermark_enabled: false,
    lock_on_blur: false,
    require_reauth: false,
    timeout_minutes: 5,
    log_events: true,
    print_allowed_paths: [],
    default_sensitivity: "confidential",
};

const Ctx = createContext({
    ...DEFAULTS,
    shieldActive: false,
    sessionRef: "",
    standalone: false,
    unlock: () => {},
    lock: () => {},
    verifyPassword: async () => false,
    updateSecurePrefs: async () => {},
});

export const useSecurePrivacy = () => useContext(Ctx);

function makeSessionRef() {
    try {
        const existing = sessionStorage.getItem("vault.session.ref");
        if (existing) return existing;
        const ref = "s" + Math.random().toString(36).slice(2, 10);
        sessionStorage.setItem("vault.session.ref", ref);
        return ref;
    } catch { return "s000000"; }
}

function detectStandalone() {
    try {
        return (
            window.matchMedia?.("(display-mode: standalone)").matches ||
            window.navigator.standalone === true
        );
    } catch { return false; }
}

export function SecurePrivacyProvider({ children }) {
    const { prefs, updatePrefs } = usePrefs();
    const { user } = useAuth();
    const stored = prefs?.secure_display || {};
    const settings = { ...DEFAULTS, ...stored };

    const [shieldActive, setShieldActive] = useState(false);
    const [manualLock, setManualLock] = useState(false);
    const inactivityTimer = useRef(null);
    const sessionRef = useMemo(makeSessionRef, []);
    const standalone = useMemo(detectStandalone, []);

    const isAuth = !!user?.id;

    const bumpInactivity = useCallback(() => {
        if (!isAuth || !settings.shield_enabled) return;
        if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
        const ms = Math.max(1, settings.timeout_minutes) * 60 * 1000;
        inactivityTimer.current = setTimeout(() => setShieldActive(true), ms);
    }, [isAuth, settings.shield_enabled, settings.timeout_minutes]);

    useEffect(() => {
        if (!isAuth) { setShieldActive(false); return; }
        if (!settings.shield_enabled) return;

        const onVis = () => { if (document.hidden) setShieldActive(true); };
        const onBlur = () => { if (settings.lock_on_blur) setShieldActive(true); };
        const onFocus = () => bumpInactivity();
        const onActivity = () => bumpInactivity();

        document.addEventListener("visibilitychange", onVis);
        window.addEventListener("blur", onBlur);
        window.addEventListener("focus", onFocus);
        window.addEventListener("mousemove", onActivity, { passive: true });
        window.addEventListener("keydown", onActivity, { passive: true });
        window.addEventListener("touchstart", onActivity, { passive: true });

        bumpInactivity();
        return () => {
            document.removeEventListener("visibilitychange", onVis);
            window.removeEventListener("blur", onBlur);
            window.removeEventListener("focus", onFocus);
            window.removeEventListener("mousemove", onActivity);
            window.removeEventListener("keydown", onActivity);
            window.removeEventListener("touchstart", onActivity);
            if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
        };
    }, [isAuth, settings.shield_enabled, settings.lock_on_blur, bumpInactivity]);

    useEffect(() => {
        if (!isAuth) return;
        const key = "vault.privacy.masked-once";
        if (settings.hide_values_default && !sessionStorage.getItem(key)) {
            updatePrefs({ hide_balance: true });
            sessionStorage.setItem(key, "1");
        }
    }, [isAuth, settings.hide_values_default, updatePrefs]);

    const lock = useCallback(() => { setManualLock(true); setShieldActive(true); }, []);
    const unlock = useCallback(() => { setManualLock(false); setShieldActive(false); bumpInactivity(); }, [bumpInactivity]);

    const verifyPassword = useCallback(async (password) => {
        try {
            const r = await api.post("/auth/verify-password", { password });
            return !!r.data?.ok;
        } catch { return false; }
    }, []);

    const updateSecurePrefs = useCallback(async (patch) => {
        const merged = { ...settings, ...patch };
        await updatePrefs({ secure_display: merged });
    }, [settings, updatePrefs]);

    // When running as an installed PWA, expose the flag on the body so
    // CSS or telemetry can react to it. Also used by PrivacyShield to
    // decide whether to show a reauth prompt.
    useEffect(() => {
        document.body.setAttribute("data-standalone", standalone ? "1" : "0");
    }, [standalone]);

    return (
        <Ctx.Provider value={{
            ...settings,
            shieldActive: isAuth && (shieldActive || manualLock),
            sessionRef,
            standalone,
            isAuth,
            unlock,
            lock,
            verifyPassword,
            updateSecurePrefs,
        }}>
            {children}
        </Ctx.Provider>
    );
}
